import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Cloud, X } from 'lucide-react';
import { clsx } from 'clsx';

export interface SyncConflictProject {
  sid: string;
  name: string;
  icon?: string;
}

interface SyncProjectsConflictModalProps {
  isOpen: boolean;
  projects: SyncConflictProject[];
  isResolving: boolean;
  onClose: () => void;
  onMatchServer: (selectedSids: string[]) => void | Promise<void>;
  onRestoreToCloud: (selectedSids: string[]) => void | Promise<void>;
}

function projectEmoji(icon?: string): string | undefined {
  if (!icon) return undefined;
  if (/^[0-9A-Fa-f]+$/.test(icon)) {
    try {
      return String.fromCodePoint(parseInt(icon, 16));
    } catch {
      return icon;
    }
  }
  return icon;
}

export const SyncProjectsConflictModal: React.FC<SyncProjectsConflictModalProps> = ({
  isOpen,
  projects,
  isResolving,
  onClose,
  onMatchServer,
  onRestoreToCloud,
}) => {
  const { t } = useTranslation('common');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(projects.map((p) => p.sid)));
      setError(null);
    }
  }, [isOpen, projects]);

  const toggleSid = (sid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const selectedSids = projects.filter((p) => selected.has(p.sid)).map((p) => p.sid);

  const runAction = async (action: 'match' | 'restore') => {
    if (selectedSids.length === 0) {
      setError(t('sessions.syncConflict.selectAtLeastOne'));
      return;
    }
    setError(null);
    try {
      if (action === 'match') await onMatchServer(selectedSids);
      else await onRestoreToCloud(selectedSids);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('unknownError'));
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isResolving) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[161] w-[min(480px,92vw)] -translate-x-1/2 -translate-y-1/2 modal-surface border border-border bg-background shadow-modal p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <Dialog.Title className="text-lg font-bold italic text-foreground">
              {t('sessions.syncConflict.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={isResolving}
                aria-label={t('close')}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            {t('sessions.syncConflict.description')}
          </p>

          <ul className="max-h-48 overflow-y-auto custom-scrollbar space-y-1 mb-4 border border-border rounded-xl p-2">
            {projects.map((project) => {
              const emoji = projectEmoji(project.icon);
              return (
                <li key={project.sid}>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(project.sid)}
                      disabled={isResolving}
                      onChange={() => toggleSid(project.sid)}
                      className="rounded border-border"
                    />
                    {emoji ? (
                      <span className="text-base leading-none" aria-hidden>
                        {emoji}
                      </span>
                    ) : null}
                    <span className="text-sm font-medium truncate">{project.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>

          {error ? (
            <p className="text-xs text-destructive mb-3" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              disabled={isResolving}
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted"
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              disabled={isResolving}
              onClick={() => void runAction('restore')}
              className={clsx(
                'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold',
                'bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50'
              )}
            >
              <Cloud size={16} />
              {isResolving
                ? t('sessions.syncConflict.restoring')
                : t('sessions.syncConflict.restoreToCloud')}
            </button>
            <button
              type="button"
              disabled={isResolving}
              onClick={() => void runAction('match')}
              className={clsx(
                'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold',
                'bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50'
              )}
            >
              <AlertTriangle size={16} />
              {isResolving
                ? t('sessions.syncConflict.matching')
                : t('sessions.syncConflict.matchServer')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
